import React, { useCallback, useEffect, useState } from 'react';

import { NextImage } from '@seuk/design-system/atoms';

import { PAGE_SIZE } from './articles-finder';
import { Pagination, PaginationItem, PaginationNavigation } from './styles';

type ArticlesFinderPaginationProps = {
  articlesCounter: number;
  onPageChange: (page: number) => void;
};

export default function ArticlesFinderPagination({ articlesCounter, onPageChange }: ArticlesFinderPaginationProps) {
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    onPageChange(currentPage);
  }, [currentPage, onPageChange]);

  const pagesAmount = Math.ceil(articlesCounter / PAGE_SIZE);
  const pages = Array.from({ length: pagesAmount }, (_, index) => index + 1);

  const goToPage = useCallback((page: number) => page !== currentPage && setCurrentPage(page), [currentPage]);
  const goNextPage = useCallback(() => currentPage > 1 && setCurrentPage((prev) => prev - 1), [currentPage]);
  const goPreviousPage = useCallback(
    () => currentPage < pagesAmount && setCurrentPage((prev) => prev + 1),
    [currentPage, pagesAmount]
  );

  return (
    <Pagination>
      <ul>
        <PaginationNavigation
          title="Previous Page"
          onClick={goNextPage}
          disabled={currentPage === 1}
          aria-disabled={currentPage === 1}
        >
          <NextImage
            src="/icons/left-arrow.svg"
            alt="left-arrow"
            width="20"
            height="20"
            unoptimized={true}
            aria-hidden={true}
          />
        </PaginationNavigation>

        {pages.map((value) => (
          <PaginationItem
            key={value}
            title={value.toString()}
            tabIndex={0}
            active={value === currentPage}
            onClick={() => goToPage(value)}
          >
            <a rel="nofollow">{value}</a>
          </PaginationItem>
        ))}

        <PaginationNavigation
          title="Next Page"
          tabIndex={0}
          onClick={goPreviousPage}
          disabled={currentPage === pagesAmount}
          aria-disabled={currentPage === pagesAmount}
        >
          <NextImage
            src="/icons/right-arrow.svg"
            alt="left-arrow"
            width="20"
            height="20"
            unoptimized={true}
            aria-hidden={true}
          />
        </PaginationNavigation>
      </ul>
    </Pagination>
  );
}
